import ApiService from '../../common/services/ApiService';

const api = new ApiService();

const USER_GROUPS_PATH = '/api/v1/user_groups';

export interface UserGroup {
  id: number;
  name: string;
  description: string | null;
}

export type UserGroupParams = Omit<UserGroup, 'id'>;

// The shape of each entry in a user's `user_groups` from the users endpoints.
export type UserGroupSummary = Pick<UserGroup, 'id' | 'name'>;

// Carries the backend's error message, plus per-field messages for validation
// failures (422), e.g. { name: ['has already been taken'] }.
export class UserGroupApiError extends Error {
  status: number;
  fieldErrors: Record<string, string[]>;

  constructor(status: number, json: { message?: string; messages?: Record<string, string[]> } | null) {
    super(json?.message || `User group request failed with status ${status}`);
    this.name = 'UserGroupApiError';
    this.status = status;
    this.fieldErrors = json?.messages ?? {};
  }
}

// Lists the current institute's groups. Only admins can read them.
export const getUserGroups = async (): Promise<UserGroup[]> => {
  const response = await api.query(USER_GROUPS_PATH);
  return response?.data ?? [];
};

// Institute admins and above only.
export const createUserGroup = async (params: UserGroupParams): Promise<UserGroup> => {
  const response = await api.post(USER_GROUPS_PATH, { user_group: params });
  if (response.status !== 201) {
    throw new UserGroupApiError(response.status, response.json);
  }
  return response.json.data;
};

// Institute admins and above only.
export const updateUserGroup = async (id: number, params: UserGroupParams): Promise<UserGroup> => {
  const response = await api.put(`${USER_GROUPS_PATH}/${id}`, { user_group: params });
  if (response.status !== 200) {
    throw new UserGroupApiError(response.status, response.json);
  }
  return response.json.data;
};

// Institute admins and above only. The backend refuses (400) to delete a group
// that still has users in it.
export const deleteUserGroup = async (id: number): Promise<void> => {
  // ApiService.delete throws on unexpected statuses but resolves a 400.
  const response: { status: number; json?: any } = await api.delete('user_groups', id);
  if (response.status === 400) {
    throw new UserGroupApiError(response.status, response.json);
  }
};
